/**
 * EmbaucheAudit — CEO 2026-09-08 emergency tool.
 *
 * "Où est passé mon candidat ?" — cherche un candidat dans toutes les
 * collections en même temps (candidates + deleted + onboarding_forms +
 * contracts + email_receipts), affiche l'état complet de chaque
 * enregistrement, et permet de fusionner deux fiches doublons.
 *
 * Use case déclencheur : Lena Ziani-Kerarti n'apparaissait plus dans
 * /embauche alors qu'elle avait signé sous "Ibtihel Ziani-Kerarti".
 * Deux fiches distinctes en base pour la même personne.
 */
import { AlertCircle, FileText, Loader2, Mail, Merge, Search, ShieldAlert, Trash2, User } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { hasRole } from "../lib/roles";
import { useAuth } from "../lib/auth";

const STATUS_COLORS = {
    new: "bg-slate-100 text-slate-700",
    to_call: "bg-blue-100 text-blue-700",
    prerec_assigned: "bg-blue-100 text-blue-700",
    validated: "bg-emerald-100 text-emerald-700",
    onboarding: "bg-orange-100 text-orange-700",
    hired: "bg-emerald-100 text-emerald-800",
    rejected: "bg-red-100 text-red-700",
    submitted: "bg-emerald-100 text-emerald-700",
    approved: "bg-emerald-100 text-emerald-800",
    needs_fix: "bg-amber-100 text-amber-800",
    in_progress: "bg-blue-100 text-blue-700",
    sent: "bg-slate-100 text-slate-700",
    signed: "bg-emerald-100 text-emerald-800",
    draft: "bg-slate-100 text-slate-700",
    prepared: "bg-blue-100 text-blue-700",
};

function StatusPill({ status }) {
    if (!status) return null;
    const cls = STATUS_COLORS[status] || "bg-slate-100 text-slate-700";
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${cls}`}>
            {status}
        </span>
    );
}

function fmt(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
}

export default function EmbaucheAudit() {
    const { user } = useAuth();
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [selectedKeep, setSelectedKeep] = useState(null);
    const [selectedMerge, setSelectedMerge] = useState(null);
    const [merging, setMerging] = useState(false);

    if (!user || !hasRole(user, "admin")) {
        return (
            <div className="p-10 text-slate-500">
                Cet outil est réservé aux admins.
            </div>
        );
    }

    const search = async (e) => {
        e?.preventDefault?.();
        if (!q || q.trim().length < 2) {
            toast.error("Saisissez au moins 2 caractères");
            return;
        }
        setLoading(true);
        setData(null);
        setSelectedKeep(null);
        setSelectedMerge(null);
        try {
            const { data: res } = await api.get(`/hiring/audit/search?q=${encodeURIComponent(q.trim())}`);
            setData(res);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Recherche impossible");
        } finally {
            setLoading(false);
        }
    };

    const doMerge = async (force = false) => {
        if (!selectedKeep || !selectedMerge || selectedKeep === selectedMerge) {
            toast.error("Sélectionnez 2 fiches distinctes (garder + fusionner)");
            return;
        }
        // CEO 2026-09-08 : « Lena Ziani-Kerarti et Ibtihel Ziani-Kerarti
        // sont JUMELLES ». On refuse la fusion si les prénoms diffèrent,
        // sauf validation explicite du CEO via un 2ᵉ confirm.
        const keepCand = data.candidates.find((c) => c.id === selectedKeep);
        const mergeCand = data.candidates.find((c) => c.id === selectedMerge);
        const differentFirst =
            keepCand && mergeCand
            && (keepCand.first_name || "").trim().toLowerCase()
               !== (mergeCand.first_name || "").trim().toLowerCase();
        if (differentFirst && !force) {
            const ok = window.confirm(
                `⚠️  PRÉNOMS DIFFÉRENTS\n\n`
                + `${keepCand.first_name} ${keepCand.last_name}  vs  ${mergeCand.first_name} ${mergeCand.last_name}\n\n`
                + `Vous êtes sur le point de fusionner deux fiches avec des prénoms différents.\n`
                + `Cela pourrait être une fratrie / des jumeaux — dans ce cas NE PAS fusionner.\n\n`
                + `Cliquez OK uniquement si vous confirmez que c'est la MÊME personne (rename).`
            );
            if (!ok) return;
            return doMerge(true);
        }
        if (!window.confirm(
            `Fusionner définitivement ?\n\n`
            + `→ La fiche à fusionner sera archivée dans deleted_candidates\n`
            + `→ Les documents, contrats et invitations Academy seront rattachés à la fiche gardée\n\n`
            + `Cette action est irréversible (mais non destructive : la fiche fusionnée reste en archive).`
        )) return;
        setMerging(true);
        try {
            const { data: res } = await api.post("/hiring/audit/merge-duplicates", {
                keep_id: selectedKeep,
                merge_id: selectedMerge,
                force,
            });
            toast.success(
                `Fusion OK — ${res.forms_repointed} fiche(s) + ${res.contracts_repointed} contrat(s) + ${res.academy_repointed} Academy rattachés`,
            );
            await search();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fusion impossible");
        } finally {
            setMerging(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 p-6 md:p-10">
            <div className="max-w-6xl mx-auto space-y-6">
                <header className="space-y-1">
                    <h1 className="text-3xl font-black text-shariti-black flex items-center gap-2">
                        <ShieldAlert className="h-7 w-7 text-shariti-orange" />
                        Audit Embauche
                    </h1>
                    <p className="text-sm text-slate-500">
                        Cherche un candidat dans TOUTES les collections en même temps —
                        candidats, corbeille, fiches, contrats, journal d'envoi mails.
                        Idéal pour comprendre pourquoi un candidat "a disparu" du pipeline.
                    </p>
                </header>

                <form onSubmit={search} className="flex items-center gap-2 bg-white rounded-2xl border-2 border-slate-200 p-2 shadow-sm">
                    <Search className="h-4 w-4 text-slate-400 ml-2" />
                    <input
                        data-testid="audit-search-input"
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Nom, prénom, email, téléphone, ID… (min. 2 chars)"
                        className="flex-1 h-10 px-2 text-sm text-shariti-black placeholder:text-slate-400 focus:outline-none"
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="audit-search-btn"
                        className="inline-flex items-center gap-1.5 rounded-xl bg-shariti-orange text-white hover:bg-shariti-orange/90 h-10 px-4 text-xs font-black uppercase tracking-widest disabled:opacity-60"
                    >
                        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                        Rechercher
                    </button>
                </form>

                {data && (
                    <div className="space-y-6" data-testid="audit-results">
                        {/* Email collision warning — jumelles / fratries */}
                        {(data.email_collisions || []).length > 0 && (
                            <section
                                data-testid="audit-collisions"
                                className="bg-amber-50 rounded-2xl border-2 border-amber-300 p-5 space-y-3"
                            >
                                <div className="flex items-center gap-2">
                                    <AlertCircle className="h-5 w-5 text-amber-700" />
                                    <h2 className="text-sm font-black uppercase tracking-widest text-amber-900">
                                        {data.email_collisions.length} adresse{data.email_collisions.length > 1 ? "s" : ""} partagée{data.email_collisions.length > 1 ? "s" : ""} entre plusieurs candidats
                                    </h2>
                                </div>
                                <p className="text-xs text-amber-800">
                                    Souvent une fratrie (jumeaux, cohabitants…). Si un mail
                                    part et n'arrive pas, c'est probablement parce que
                                    l'autre personne a déjà cliqué / archivé / marqué spam
                                    depuis la même boîte.
                                </p>
                                <div className="space-y-2">
                                    {data.email_collisions.map((coll) => (
                                        <div key={coll.email} className="bg-white rounded-lg border border-amber-200 p-3">
                                            <div className="text-sm font-black text-amber-900 mb-1">{coll.email}</div>
                                            <div className="flex flex-wrap gap-2">
                                                {coll.candidates.map((c) => (
                                                    <a
                                                        key={c.id}
                                                        href={`/candidats/${c.id}`}
                                                        className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 border border-slate-200 hover:border-shariti-orange hover:bg-shariti-orange-soft/30 px-2.5 py-1 text-xs font-bold text-shariti-black transition"
                                                    >
                                                        {c.first_name} {c.last_name}
                                                        <StatusPill status={c.status} />
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Counts summary */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            {[
                                ["Candidats", data.counts.candidates, User],
                                ["Corbeille", data.counts.deleted_candidates, Trash2],
                                ["Fiches", data.counts.onboarding_forms, FileText],
                                ["Contrats", data.counts.contracts, FileText],
                                ["Mails", data.counts.email_receipts, Mail],
                            ].map(([label, n, Icon]) => (
                                <div key={label} className="bg-white rounded-xl border border-slate-200 p-3 text-center">
                                    <Icon className="h-4 w-4 text-slate-400 mx-auto mb-1" />
                                    <div className="text-xl font-black text-shariti-black">{n}</div>
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
                                </div>
                            ))}
                        </div>

                        {/* Candidates section */}
                        {data.candidates.length > 0 && (
                            <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                                <header className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                                    <h2 className="text-sm font-black uppercase tracking-widest text-slate-700">
                                        Candidats ({data.candidates.length})
                                    </h2>
                                    {data.candidates.length === 1 && (
                                        <div className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                                            ✓ Aucune fusion à faire — 1 seul candidat trouvé
                                        </div>
                                    )}
                                </header>
                                {data.candidates.length >= 2 && (
                                    <div className="px-5 py-3 bg-amber-50/50 border-b border-amber-100 text-[11px] text-slate-700 space-y-1">
                                        <div className="font-black uppercase tracking-widest text-amber-800 text-[10px]">
                                            💡 Comment décider
                                        </div>
                                        <div>
                                            <strong className="text-emerald-700">→ Ne rien faire</strong> si ce sont <strong>2 personnes différentes</strong> (jumeaux, homonymes, cousin&nbsp;: ex&nbsp;Lena&nbsp;/&nbsp;Ibtihel Ziani-Kerarti). Le système sait vivre avec des fiches distinctes.
                                        </div>
                                        <div>
                                            <strong className="text-shariti-orange">→ Fusionner</strong> uniquement si vous voyez <strong>la même personne dupliquée</strong> (même prénom + nom + tél + email). Cliquez d&apos;abord <em>Garder</em> sur la fiche à conserver, puis <em>Fusionner</em> sur la fiche à supprimer.
                                        </div>
                                    </div>
                                )}
                                <div className="divide-y divide-slate-100">
                                    {data.candidates.map((c) => (
                                        <div
                                            key={c.id}
                                            data-testid={`audit-candidate-${c.id}`}
                                            className="px-5 py-3 flex items-center gap-4 hover:bg-slate-50 transition"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <a href={`/candidats/${c.id}`} className="font-black text-shariti-black hover:text-shariti-orange">
                                                        {c.first_name} {c.last_name}
                                                    </a>
                                                    <StatusPill status={c.status} />
                                                    {c.set_aside && <span className="text-[9px] font-black uppercase text-red-700 bg-red-100 px-1.5 py-0.5 rounded">set_aside</span>}
                                                    {c.in_talent_pool && <span className="text-[9px] font-black uppercase text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">vivier</span>}
                                                    {c.removed_from_onboarding_at && <span className="text-[9px] font-black uppercase text-red-700 bg-red-100 px-1.5 py-0.5 rounded">retiré du circuit</span>}
                                                </div>
                                                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                                                    <span>{c.email}</span>
                                                    <span>{c.phone}</span>
                                                    <span className="text-slate-400">ID {c.id.slice(0, 8)}…</span>
                                                    <span className="text-slate-400">Créé {fmt(c.created_at)}</span>
                                                </div>
                                            </div>
                                            {data.candidates.length >= 2 && (
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedKeep(c.id === selectedKeep ? null : c.id)}
                                                        data-testid={`audit-keep-${c.id}`}
                                                        title="Sélectionner cette fiche comme la fiche à conserver (avant de fusionner un vrai doublon)"
                                                        className={`h-7 px-2.5 rounded-full text-[10px] font-black uppercase tracking-widest transition ${
                                                            selectedKeep === c.id
                                                                ? "bg-emerald-600 text-white"
                                                                : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                                                        }`}
                                                    >
                                                        {selectedKeep === c.id ? "✓ À conserver" : "Conserver cette fiche"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedMerge(c.id === selectedMerge ? null : c.id)}
                                                        data-testid={`audit-merge-${c.id}`}
                                                        title="Sélectionner cette fiche à supprimer / fusionner dans l'autre (uniquement si vrai doublon)"
                                                        className={`h-7 px-2.5 rounded-full text-[10px] font-black uppercase tracking-widest transition ${
                                                            selectedMerge === c.id
                                                                ? "bg-shariti-orange text-white"
                                                                : "bg-orange-50 text-shariti-orange hover:bg-orange-100 border border-orange-200"
                                                        }`}
                                                    >
                                                        {selectedMerge === c.id ? "✓ À fusionner" : "Fusionner dans l'autre"}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {selectedKeep && selectedMerge && selectedKeep !== selectedMerge && (
                                    <footer className="px-5 py-3 bg-orange-50/40 border-t border-orange-200 flex items-center justify-between gap-3">
                                        <div className="text-xs text-slate-700">
                                            <strong className="text-emerald-700">Garder :</strong> {selectedKeep.slice(0, 12)}…
                                            {" · "}
                                            <strong className="text-shariti-orange">Fusionner :</strong> {selectedMerge.slice(0, 12)}…
                                        </div>
                                        <button
                                            type="button"
                                            onClick={doMerge}
                                            disabled={merging}
                                            data-testid="audit-do-merge"
                                            className="inline-flex items-center gap-1.5 rounded-full bg-shariti-orange text-white h-8 px-4 text-[11px] font-black uppercase tracking-widest disabled:opacity-60"
                                        >
                                            {merging ? <Loader2 className="h-3 w-3 animate-spin" /> : <Merge className="h-3 w-3" />}
                                            Fusionner
                                        </button>
                                    </footer>
                                )}
                            </section>
                        )}

                        {/* Deleted */}
                        {data.deleted_candidates.length > 0 && (
                            <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                                <header className="px-5 py-3 border-b border-slate-100">
                                    <h2 className="text-sm font-black uppercase tracking-widest text-red-700">
                                        Corbeille ({data.deleted_candidates.length})
                                    </h2>
                                </header>
                                <div className="divide-y divide-slate-100">
                                    {data.deleted_candidates.map((c) => (
                                        <div key={c.id} className="px-5 py-3">
                                            <div className="font-bold text-shariti-black">{c.first_name} {c.last_name}</div>
                                            <div className="text-xs text-slate-500 mt-0.5">{c.email} · {c.phone}</div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Forms */}
                        {data.onboarding_forms.length > 0 && (
                            <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                                <header className="px-5 py-3 border-b border-slate-100">
                                    <h2 className="text-sm font-black uppercase tracking-widest text-slate-700">
                                        Fiches d'embauche ({data.onboarding_forms.length})
                                    </h2>
                                </header>
                                <div className="divide-y divide-slate-100">
                                    {data.onboarding_forms.map((f) => (
                                        <div key={f.id} className="px-5 py-3 text-sm">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <StatusPill status={f.status} />
                                                <span className="text-shariti-black font-bold">
                                                    {f.data?.first_name || "—"} {f.data?.last_name || ""}
                                                </span>
                                                <span className="text-slate-500 text-xs">{f.data?.email || f.candidate_email}</span>
                                            </div>
                                            <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                                                <span>Cand. ID {f.candidate_id?.slice(0, 8)}…</span>
                                                <span>Envoyé {fmt(f.sent_at)}</span>
                                                <span>Soumis {fmt(f.submitted_at)}</span>
                                                {f.last_needs_fix_at && <span className="text-amber-700">Correction {fmt(f.last_needs_fix_at)}</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Contracts */}
                        {data.contracts.length > 0 && (
                            <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                                <header className="px-5 py-3 border-b border-slate-100">
                                    <h2 className="text-sm font-black uppercase tracking-widest text-slate-700">
                                        Contrats ({data.contracts.length})
                                    </h2>
                                </header>
                                <div className="divide-y divide-slate-100">
                                    {data.contracts.map((c) => (
                                        <div key={c.id} className="px-5 py-3 text-sm flex items-center gap-3">
                                            <StatusPill status={c.status} />
                                            <span className="font-bold text-shariti-black">{c.contract_code || "—"}</span>
                                            <span className="text-slate-500 text-xs">Cand. {c.candidate_id?.slice(0, 8)}…</span>
                                            <span className="text-slate-500 text-xs ml-auto">
                                                {c.signed_at ? `Signé ${fmt(c.signed_at)}` : `Créé ${fmt(c.created_at)}`}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Email receipts */}
                        {data.email_receipts.length > 0 && (
                            <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                                <header className="px-5 py-3 border-b border-slate-100">
                                    <h2 className="text-sm font-black uppercase tracking-widest text-slate-700">
                                        Mails envoyés ({data.email_receipts.length})
                                    </h2>
                                </header>
                                <div className="divide-y divide-slate-100 max-h-96 overflow-auto">
                                    {data.email_receipts.map((r, i) => (
                                        <div key={i} className="px-5 py-2.5 text-xs">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black uppercase ${
                                                    r.provider === "bounced" || r.error
                                                        ? "bg-red-100 text-red-700"
                                                        : r.provider === "dry-run" || r.provider === "preview-dry-run"
                                                        ? "bg-slate-100 text-slate-500"
                                                        : "bg-emerald-100 text-emerald-700"
                                                }`}>
                                                    {r.provider || "?"}
                                                </span>
                                                <span className="font-bold text-shariti-black">{r.to}</span>
                                                <span className="text-slate-400">{fmt(r.sent_at)}</span>
                                            </div>
                                            <div className="text-slate-500 mt-0.5 truncate">{r.subject}</div>
                                            {r.error && (
                                                <div className="text-red-600 mt-0.5 flex items-center gap-1">
                                                    <AlertCircle className="h-3 w-3" />
                                                    {r.error}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {Object.values(data.counts).every((n) => n === 0) && (
                            <div className="text-center text-slate-500 py-10 bg-white rounded-2xl border border-slate-200">
                                Aucun résultat trouvé pour "<strong>{data.query}</strong>"
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
